// src/Components/AccountUpgradeModal.jsx
import React, { useState } from "react";
import accountUpgradeService from "../Services/accountUpgradeService";
import { authErrorMessage } from "../Services/authErrors";
import BottomSheet from "./BottomSheet";
import { TextField, PasswordField, PrimaryButton, FormError, OrDivider, GoogleButton } from "./auth/AuthFields";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_FIELD_ERRORS = ["email_exists", "invalid_email"];

const AccountUpgradeModal = ({ isOpen, onClose, onSuccess, onSwitchToSignIn, subtitle = "Save your rooms and coins, and play on any device" }) => {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");

  const busy = loading || googleLoading;

  const resetForm = () => {
    setDisplayName("");
    setEmail("");
    setPassword("");
    setFieldErrors({});
    setError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // No await before the service call: a Google sign-up as a visitor has to
  // open its popup inside the click, or Safari blocks it.
  const handleGoogleUpgrade = async () => {
    setGoogleLoading(true);
    setError("");
    setFieldErrors({});

    try {
      const result = await accountUpgradeService.upgradeWithGoogle();
      if (result.success) {
        if (onSuccess) onSuccess(result);
        handleClose();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error("Google upgrade error:", err);
      setError(authErrorMessage(err, "We couldn't connect your Google account"));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleEmailUpgrade = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!displayName.trim()) errors.name = "Enter a display name.";
    if (!email.trim()) errors.email = "Enter your email.";
    else if (!EMAIL_RE.test(email.trim())) errors.email = "That email doesn't look right.";
    if (!password) errors.password = "Choose a password.";
    else if (password.length < 6) errors.password = "Use at least 6 characters.";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    setError("");

    try {
      const result = await accountUpgradeService.upgradeWithEmail(email.trim(), password);

      if (result.success) {
        await accountUpgradeService.updateUserDocumentAfterUpgrade(result.user, {
          displayName: displayName.trim(),
        });
        if (onSuccess) onSuccess(result);
        handleClose();
      } else if (EMAIL_FIELD_ERRORS.includes(result.error)) {
        setFieldErrors({ email: result.message });
      } else if (result.error === "weak_password") {
        setFieldErrors({ password: result.message });
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error("Email upgrade error:", err);
      setError(authErrorMessage(err, "We couldn't create your account"));
    } finally {
      setLoading(false);
    }
  };

  const switchToSignIn = () => {
    resetForm();
    onSwitchToSignIn();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={busy ? () => {} : handleClose}
      title="Create account"
      subtitle={subtitle}
    >
      <GoogleButton onClick={handleGoogleUpgrade} loading={googleLoading} disabled={loading} />
      <OrDivider />
      <form onSubmit={handleEmailUpgrade} noValidate className="space-y-4">
        <TextField
          label="Display name"
          autoComplete="nickname"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={busy}
          error={fieldErrors.name}
          hint="Used to fill in the name when you create a room"
          placeholder="What should we call you?"
        />
        <TextField
          label="Email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          error={fieldErrors.email}
          placeholder="you@example.com"
        />
        <PasswordField
          label="Password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
          error={fieldErrors.password}
          hint="At least 6 characters"
          placeholder="Create a password"
        />
        <FormError>{error}</FormError>
        <PrimaryButton loading={loading} loadingLabel="Creating account…" disabled={busy}>
          Create account
        </PrimaryButton>
      </form>
      {onSwitchToSignIn && (
        <p className="mt-4 text-center text-sm text-brand-textSecondary">
          Already have an account?{" "}
          <button
            type="button"
            onClick={switchToSignIn}
            disabled={busy}
            className="min-h-[44px] font-semibold text-brand-accent hover:underline"
          >
            Sign in
          </button>
        </p>
      )}
    </BottomSheet>
  );
};

export default AccountUpgradeModal;
